import { useState } from 'react';
import { ChevronDown, ChevronUp, Info } from 'lucide-react';
import { Button, InfoAlert } from '@/components/ui';
import { CarouselHeader } from './CarouselHeader';
import { VideoPlaceholder } from './VideoPlaceholder';
import { WalkthroughSenderList } from './WalkthroughSenderList';
import type { WalkthroughSenderListItem } from './WalkthroughSenderList';

export interface Step2ForwardEmailProps {
  senders: WalkthroughSenderListItem[];
  onBack: () => void;
  onNext: () => void;
  onClose: () => void;
  /** Optional override for the demo video URL. Falls back to placeholder. */
  videoSrc?: string;
}

/**
 * Step 2 — "Forward the email to yourself". Shows the demo video plus a
 * collapsible read-only sender list for reference (AC 13). X closes
 * without writing dismissed state — the parent handles the toast.
 */
export function Step2ForwardEmail({
  senders,
  onBack,
  onNext,
  onClose,
  videoSrc,
}: Step2ForwardEmailProps) {
  const [expanded, setExpanded] = useState(false);
  return (
    // "Next" is PINNED below the scroll region, not the last child inside it
    // (YAR-362). This step is a fixed-height box whose content overflows: the
    // video alone is 446px of a ~717px budget on a 390x844 phone, and with the
    // header, copy, accordion and alert above it the button landed off-screen.
    // It was reachable by scrolling, but nothing said so — QA saw only the X,
    // which quits the walkthrough entirely.
    //
    // Fixed WITHOUT touching the design: the video keeps its exact Figma
    // dimensions (its `shrink-0` is load-bearing — letting it shrink is what
    // squashed it to a strip in an earlier round) and no copy moves. Same
    // shape OnboardingLayout uses, plus the safe-area inset every
    // bottom-pinned control in this app needs, or the home indicator sits on
    // top of it.
    <div className="flex flex-col max-h-[calc(85dvh)]">
      <div className="min-h-0 flex-1 overflow-y-auto flex flex-col gap-4 p-5 pb-0">
        <CarouselHeader
          stepIndex={2}
          onBack={onBack}
          onClose={onClose}
        />

        <div className="flex flex-col gap-2">
          <h2 className="text-xl font-semibold text-foreground">
            Forward the email to yourself
          </h2>
          <p className="text-base text-foreground">
            Open Gmail app and find the older email. Tap the three-dot menu or
            Forward button below. Send it to your own Gmail address — the same
            one connected to Yaruta.
          </p>
        </div>

        <VideoPlaceholder
          src={videoSrc}
          ariaLabel="How to forward an email in the Gmail app"
        />

        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            aria-controls="aha-step2-senders"
            className="flex items-center justify-between py-2 cursor-pointer"
          >
            <span className="text-base font-semibold text-foreground">
              View Approved Senders
            </span>
            {expanded ? (
              <ChevronUp
                size={20}
                className="text-icon-default"
              />
            ) : (
              <ChevronDown
                size={20}
                className="text-icon-default"
              />
            )}
          </button>

          {expanded && (
            <div
              id="aha-step2-senders"
              className="flex flex-col gap-3"
            >
              <WalkthroughSenderList items={senders} />
            </div>
          )}

          {/* The info alert sits OUTSIDE the accordion (kelvin's review)
            so the constraint stays visible whether or not the user has
            expanded the senders list. */}
          <InfoAlert icon={<Info size={16} />}>
            You can only forward one email at a time. Repeat these steps for
            each email you want Yaruta to review.
          </InfoAlert>
        </div>
      </div>

      <div className="shrink-0 px-5 pt-3 pb-[max(1.25rem,env(safe-area-inset-bottom))]">
        <Button
          variant="primary"
          fullWidth
          onClick={onNext}
        >
          Next
        </Button>
      </div>
    </div>
  );
}
